import React, { useState, useCallback } from 'react';
import { Star } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

const Message = ({ role, content, timestamp, onStar, isStarred = false, compact = false }) => {
  const [isSpinning, setIsSpinning] = useState(false);

  const handleMascotClick = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setTimeout(() => setIsSpinning(false), 1200);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Normalize TeX delimiters: support \( \) and \[ \] by converting to $ $ and $$ $$
  const normalizeMath = (t) => {
    if (!t) return '';
    // Display math: \[ ... ] -> $$ ... $$
    t = t.replace(/\\\[((?:.|\n)+?)\\\]/g, (_, m) => `$$${m}$$`);
    // Inline math: \( ... ) -> $ ... $
    t = t.replace(/\\\((.+?)\\\)/g, (_, m) => `$${m}$`);
    return t;
  };
  const contentNormalized = normalizeMath(content || '');

  // Collapsible long content
  const [expanded, setExpanded] = useState(false);
  const shouldCollapse = (() => {
    const len = contentNormalized.length;
    const lines = (contentNormalized.match(/\n/g) || []).length + 1;
    return len > 900 || lines > 20;
  })();

  const CodeBlock = ({ inline, className, children, ...props }) => {
    const [copied, setCopied] = useState(false);
    const codeText = String(children).replace(/\n$/, '');

    const handleCopy = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(codeText);
        setCopied(true);
        window.dispatchEvent(new CustomEvent('app-toast', { detail: 'Copied!' }));
        setTimeout(() => setCopied(false), 1200);
      } catch (e) {
        window.dispatchEvent(new CustomEvent('app-toast', { detail: 'Copy failed' }));
      }
    }, [codeText]);

    if (inline) {
      return <code className={className} {...props}>{children}</code>;
    }

    return (
      <div className="lrbrain-codeblock">
        <button className="lrbrain-copy-button" onClick={handleCopy} type="button">
          {copied ? '✓ Copied' : 'Copy'}
        </button>
        <pre><code className={className} {...props}>{children}</code></pre>
      </div>
    );
  };

  // Map role to CSS class names (assistant -> ai)
  const roleClass = role === 'assistant' ? 'ai' : role;

  if (roleClass === 'system') {
    return (
      <div className="lrbrain-message-container system">
        <div className="lrbrain-message system">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[[rehypeKatex, { strict: false }]]}
          >
            {contentNormalized}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  return (
    <div className={`lrbrain-message-container ${roleClass}`}>
      {roleClass === 'ai' && (
        <img
          src="/lrbrain-logo.png"
          alt="LRB"
          className={`lrbrain-mascot-avatar${compact ? ' small' : ''}${isSpinning ? ' spin-once' : ''}`}
          onClick={handleMascotClick}
        />
      )}
      <div className="lrbrain-message-content">
        <div className={`lrbrain-message ${roleClass}${compact ? ' compact' : ''}`}>
          {/* Quick actions */}
          {onStar && (
            <div className="lrbrain-msg-actions">
              <button
                type="button"
                className={`star ${isStarred ? 'active' : ''}`}
                title={isStarred ? 'Unstar' : 'Star'}
                onClick={(e) => { e.stopPropagation(); onStar(); }}
              >
                <Star size={15} fill={isStarred ? 'currentColor' : 'none'} />
              </button>
            </div>
          )}
          {roleClass === 'user' ? (
            <span>{content || ''}</span>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[[rehypeKatex, { strict: false }]]}
              components={{
                code: CodeBlock,
                a: ({ node, children, ...props }) => (
                  <a {...props} target="_blank" rel="noreferrer noopener">{children}</a>
                ),
                p: ({ node, children, ...props }) => (
                  <p {...props} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</p>
                )
              }}
            >
              {shouldCollapse && !expanded
                ? contentNormalized.split('\n').slice(0, 18).join('\n') + '\n\n...'
                : contentNormalized}
            </ReactMarkdown>
          )}
          {shouldCollapse && roleClass !== 'user' && (
            <div style={{ marginTop: '0.5rem' }}>
              <button
                type="button"
                className="lrbrain-collapse-btn"
                onClick={() => setExpanded(e => !e)}
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            </div>
          )}
        </div>
        <div className="lrbrain-message-timestamp">
          {formatTimestamp(timestamp)}
        </div>
      </div>
    </div>
  );
};

export default Message;
